// Bank brand config — canonical bank name → visual identity

export type BankBrand = {
  abbr: string;       // 1-3 letter abbreviation (fallback)
  color: string;      // Background color (hex) — used for fallback circle
  textColor: string;  // Text color on the fallback circle (hex)
  logo?: string;      // Path to SVG logo in /public/bank-logos/
};

export const BANK_BRANDS: Record<string, BankBrand> = {
  // Traditional banks
  "GTBank":         { abbr: "GT",  color: "#D94F00", textColor: "#FFFFFF", logo: "/bank-logos/gtbank.svg" },
  "Access Bank":    { abbr: "AC",  color: "#F37021", textColor: "#FFFFFF", logo: "/bank-logos/access-bank.svg" },
  "Zenith Bank":    { abbr: "ZB",  color: "#E2231A", textColor: "#FFFFFF", logo: "/bank-logos/zenith-bank.svg" },
  "UBA":            { abbr: "UB",  color: "#D42E12", textColor: "#FFFFFF", logo: "/bank-logos/uba.svg" },
  "First Bank":     { abbr: "FB",  color: "#162D4C", textColor: "#E4B935", logo: "/bank-logos/first-bank.svg" },
  "Fidelity Bank":  { abbr: "FD",  color: "#00254D", textColor: "#FFFFFF", logo: "/bank-logos/fidelity-bank.svg" },
  "Union Bank":     { abbr: "UN",  color: "#009FE3", textColor: "#FFFFFF", logo: "/bank-logos/union-bank.svg" },
  "Stanbic IBTC":   { abbr: "SI",  color: "#0033A0", textColor: "#FFFFFF", logo: "/bank-logos/stanbic-ibtc.svg" },
  "Polaris Bank":   { abbr: "PL",  color: "#7B2D8E", textColor: "#FFFFFF", logo: "/bank-logos/polaris-bank.svg" },
  "FCMB":           { abbr: "FC",  color: "#5C2D91", textColor: "#FFFFFF", logo: "/bank-logos/fcmb.svg" },
  "Ecobank":        { abbr: "EC",  color: "#0072BC", textColor: "#FFFFFF", logo: "/bank-logos/ecobank.svg" },
  "Heritage Bank":  { abbr: "HB",  color: "#006B3F", textColor: "#FFFFFF", logo: "/bank-logos/heritage-bank.svg" },
  "Wema Bank":      { abbr: "WM",  color: "#AE328E", textColor: "#FFFFFF", logo: "/bank-logos/wema-bank.svg" },
  "Sterling Bank":  { abbr: "ST",  color: "#CF2E2E", textColor: "#FFFFFF", logo: "/bank-logos/sterling-bank.svg" },
  "Providus Bank":  { abbr: "PV",  color: "#1B3C6E", textColor: "#FFFFFF", logo: "/bank-logos/providus-bank.svg" },
  "Keystone Bank":  { abbr: "KS",  color: "#0066B3", textColor: "#FFFFFF", logo: "/bank-logos/keystone-bank.svg" },
  "Unity Bank":     { abbr: "UT",  color: "#008752", textColor: "#FFFFFF", logo: "/bank-logos/unity-bank.svg" },
  "Globus Bank":    { abbr: "GL",  color: "#1A1A6C", textColor: "#FFFFFF", logo: "/bank-logos/globus-bank.svg" },
  "Titan Trust Bank": { abbr: "TT", color: "#1C3A5F", textColor: "#FFFFFF", logo: "/bank-logos/titan-trust-bank.svg" },
  "TAJ Bank":       { abbr: "TJ",  color: "#006233", textColor: "#FFFFFF", logo: "/bank-logos/taj-bank.svg" },
  "SunTrust Bank":  { abbr: "SU",  color: "#F7941D", textColor: "#FFFFFF", logo: "/bank-logos/suntrust-bank.svg" },
  // Digital banks / fintechs
  "Nomba":          { abbr: "NM",  color: "#FFCC00", textColor: "#0D0D0D", logo: "/bank-logos/nomba.svg" },
  "OPay":           { abbr: "OP",  color: "#1DCF9F", textColor: "#FFFFFF", logo: "/bank-logos/opay.svg" },
  "Kuda":           { abbr: "KU",  color: "#40196D", textColor: "#FFFFFF", logo: "/bank-logos/kuda.svg" },
  "PalmPay":        { abbr: "PP",  color: "#7C3AED", textColor: "#FFFFFF", logo: "/bank-logos/palmpay.svg" },
  "Moniepoint":     { abbr: "MP",  color: "#0357EE", textColor: "#FFFFFF", logo: "/bank-logos/moniepoint.svg" },
  "Carbon":         { abbr: "CB",  color: "#00C853", textColor: "#FFFFFF", logo: "/bank-logos/carbon.svg" },
  "FairMoney":      { abbr: "FM",  color: "#6200EA", textColor: "#FFFFFF", logo: "/bank-logos/fairmoney.svg" },
};

// Aliases for fuzzy matching — maps alternate names → canonical key
const ALIASES: Record<string, string> = {
  "guaranty trust bank": "GTBank",
  "guaranty trust": "GTBank",
  "gtb": "GTBank",
  "gt bank": "GTBank",
  "gtworld": "GTBank",
  "access more": "Access Bank",
  "accessmore": "Access Bank",
  "diamond bank": "Access Bank",
  "zenith": "Zenith Bank",
  "eazybanking": "Zenith Bank",
  "united bank for africa": "UBA",
  "united bank of africa": "UBA",
  "firstbank": "First Bank",
  "first bank of nigeria": "First Bank",
  "fbn": "First Bank",
  "fidelity": "Fidelity Bank",
  "stanbic": "Stanbic IBTC",
  "stanbic ibtc bank": "Stanbic IBTC",
  "polaris": "Polaris Bank",
  "vulte": "Polaris Bank",
  "first city monument bank": "FCMB",
  "first city monument": "FCMB",
  "alat": "Wema Bank",
  "alat by wema": "Wema Bank",
  "wema": "Wema Bank",
  "sterling": "Sterling Bank",
  "onebank": "Sterling Bank",
  "providus": "Providus Bank",
  "keystone": "Keystone Bank",
  "unity bank plc": "Unity Bank",
  "globus": "Globus Bank",
  "titan trust": "Titan Trust Bank",
  "taj": "TAJ Bank",
  "suntrust": "SunTrust Bank",
  "opay digital": "OPay",
  "opera pay": "OPay",
  "kuda bank": "Kuda",
  "kuda microfinance": "Kuda",
  "palm pay": "PalmPay",
  "palmpay limited": "PalmPay",
  "monie point": "Moniepoint",
  "moniepoint inc": "Moniepoint",
  "paylater": "Carbon",
  "carbon finance": "Carbon",
  "fair money": "FairMoney",
  "nomba inc": "Nomba",
  "kudi": "Nomba",
  "kudi money": "Nomba",
  "nomba microfinance": "Nomba",
};

/**
 * Fuzzy lookup: given any bank name string, returns the BankBrand or null.
 * Handles canonical names, aliases, and partial substring matches.
 */
export function getBankBrand(name: string | undefined | null): BankBrand | null {
  if (!name) return null;

  // Direct match
  if (BANK_BRANDS[name]) return BANK_BRANDS[name];

  // Alias match
  const lower = name.toLowerCase().trim();
  const aliasKey = ALIASES[lower];
  if (aliasKey && BANK_BRANDS[aliasKey]) return BANK_BRANDS[aliasKey];

  // Substring match — check if any canonical name or alias is contained
  for (const [canonical, brand] of Object.entries(BANK_BRANDS)) {
    if (lower.includes(canonical.toLowerCase()) || canonical.toLowerCase().includes(lower)) {
      return brand;
    }
  }
  for (const [alias, canonical] of Object.entries(ALIASES)) {
    if (lower.includes(alias)) {
      return BANK_BRANDS[canonical] ?? null;
    }
  }

  return null;
}
